import { apolloClient, isServerDownError, type DocumentNode } from '@/vendor/apollo-client'

import { match } from 'ts-pattern'
import { UserCollection } from '@/stores/user-collection/index.ts'
import { useAuthStore } from '@/stores/auth.ts'
import { ref, type Ref } from 'vue'
import {
  CREATE_USER_COLLECTION,
  REMOVE_USER_COLLECTION,
  UPDATE_USER_COLLECTION,
  UPDATE_USER_READ_STATUS,
} from '@/queries'
import { ResultOf, VariablesOf } from '@graphql-typed-document-node/core'
import { storeToRefs } from 'pinia'
import {
  UserReleaseEntryStatusCollectionStatusType,
  UserReleaseEntryStatusCurrentReadStatusType,
  UserReleaseReadStatusStatusType,
} from '../../../__generated__/graphql.ts'

// What callers hand to createCollectionEntry. The store mints the clientId; the
// server ids (collectionId/readId) are filled in by creationSuccess after sync.
export type CreateCollectionEntryData = {
  collectionId: string | null
  readId: string | null
  entryId: string
  ownedMap: UserCollection['ownedMap']
  miscOwnedMap: UserCollection['miscOwnedMap']
  latestReadMap: UserCollection['latestReadMap']
  collectionStatus: UserReleaseEntryStatusCollectionStatusType
  currentReadStatus: UserReleaseEntryStatusCurrentReadStatusType
  sortedVolumeNumbers: string[]
  notReleasedVolumeNumbers: string[]
  entryCompleted: boolean
  startYear: number | null
  startMonth: number | null
  endYear: number | null
  endMonth: number | null
  suppressSiblingToast?: boolean
}

// The stored create action also carries the minted clientId — the stable local
// identity every downstream edit references until (and after) the server answers.
export type StoredCreateCollectionEntryData = CreateCollectionEntryData & { clientId: string }

// Actions reference the entity by clientId, never a server id. entryId stays as
// the catalog key used by the optimistic overlay / list-prune / sibling refresh.
// The server ids are resolved live at sync time (see ResolvedServerIds).
export type UserCollectionActionType =
  | {
      type: 'createCollectionEntry'
      data: StoredCreateCollectionEntryData
    }
  | {
      type: 'removeCollectionEntry'
      data: { clientId: string; entryId: string; deletionDate: string }
    }
  | {
      type: 'updateCollectionData'
      data: {
        clientId: string
        entryId: string
        collectionData: Partial<UserCollection>
        suppressSiblingToast?: boolean
      }
    }
  | {
      type: 'updateEntryReadMap'
      data: {
        clientId: string
        entryId: string
        latestReadMap: UserCollection['latestReadMap']
      }
    }

export type UserCollectionActionSync = {
  type: UserCollectionActionType
  sync: { id: string; synced: boolean }
}

// Server ids resolved from the target entity (or its create action) at sync time.
export type ResolvedServerIds = { collectionId: string | null; readId: string | null }

export type SyncStatus =
  | { type: 'inProgress'; syncId: string }
  | {
      type: 'success'
      syncId: string
    }
  | {
      type: 'creationSuccess'
      syncId: string
      creationResult: { collectionId: string; latestReadId: string }
    }
  | { type: 'error'; syncId: string; error: SyncError }
  | null
// 'serverDown' is a transient transport/5xx failure — the edit could not be
// pushed and must stay queued. 'unknown' is a real rejection (the server answered
// with an error) — the edit can't succeed and is rolled back.
export type SyncError = { type: 'unknown' | 'serverDown'; error: Error }

export type CollectionSyncState = {
  syncStatus: Ref<SyncStatus> | null
  startSync: (action: UserCollectionActionSync, resolved: ResolvedServerIds) => void
}

// Misc (bundle/slipcase) changes can belong to a shared collection group; the
// server then updates the user's statuses on OTHER entries. The caller uses the
// returned ids to find those sibling entries and refresh them.
export type MiscChangeDirection = 'added' | 'removed' | 'mixed'

export function getMiscChangeDirection(
  action: UserCollectionActionType,
): MiscChangeDirection | null {
  const miscMap = match(action)
    .returnType<UserCollection['miscOwnedMap'] | undefined>()
    .with({ type: 'createCollectionEntry' }, ({ data }) => data.miscOwnedMap)
    .with({ type: 'updateCollectionData' }, ({ data }) => data.collectionData.miscOwnedMap)
    .otherwise(() => undefined)
  const quantities = Object.values(miscMap ?? {}).map((value) => value.quantity)
  if (quantities.length === 0) return null
  const hasAdd = quantities.some((quantity) => quantity > 0)
  const hasRemove = quantities.some((quantity) => quantity === 0)
  if (hasAdd && hasRemove) return 'mixed'
  return hasAdd ? 'added' : 'removed'
}

export function shouldSuppressSiblingToast(action: UserCollectionActionType): boolean {
  return match(action)
    .returnType<boolean>()
    .with({ type: 'createCollectionEntry' }, ({ data }) => data.suppressSiblingToast === true)
    .with({ type: 'updateCollectionData' }, ({ data }) => data.suppressSiblingToast === true)
    .otherwise(() => false)
}

export function getChangedMiscIdsFromAction(action: UserCollectionActionType): string[] {
  return match(action)
    .returnType<string[]>()
    .with({ type: 'createCollectionEntry' }, ({ data }) => Object.keys(data.miscOwnedMap ?? {}))
    .with({ type: 'updateCollectionData' }, ({ data }) =>
      Object.keys(data.collectionData.miscOwnedMap ?? {}),
    )
    .otherwise(() => [])
}

export function buildCreateUserCollectionInput(
  data: CreateCollectionEntryData,
  now: string,
): VariablesOf<typeof CREATE_USER_COLLECTION>['data'] {
  return {
    entry: { connect: { id: data.entryId } },
    readStatus: {
      create: [
        {
          status:
            (data.currentReadStatus as unknown as UserReleaseReadStatusStatusType) ??
            UserReleaseReadStatusStatusType.Planned,
          readNumber: 1,
          readStatus: data.latestReadMap,
          lastReadChanged: now,
          startedRead: now,
        },
      ],
    },
    collectionStatus:
      data.collectionStatus ?? UserReleaseEntryStatusCollectionStatusType.Collecting,
    currentReadStatus:
      (data.currentReadStatus as unknown as UserReleaseEntryStatusCurrentReadStatusType) ??
      UserReleaseEntryStatusCurrentReadStatusType.Planned,
    lastReadChanged: now,
    lastCollectionChanged: now,
    itemStatus: data.ownedMap,
    miscItemStatus: data.miscOwnedMap,
  }
}

export function buildUpdateUserCollectionInput(
  collectionData: Partial<UserCollection>,
  now: string,
): VariablesOf<typeof UPDATE_USER_COLLECTION>['data'] {
  // Any possession change (volumes, misc/bundles or the status itself) must
  // carry lastCollectionChanged: the server's event emission and the
  // shared-collection twin sync are gated on that timestamp.
  const possessionChanged = Boolean(
    collectionData.ownedMap || collectionData.miscOwnedMap || collectionData.collectionStatus,
  )
  return {
    ...(collectionData.ownedMap ? { itemStatus: collectionData.ownedMap } : {}),
    ...(collectionData.collectionStatus
      ? { collectionStatus: collectionData.collectionStatus }
      : {}),
    ...(collectionData.currentReadStatus
      ? { currentReadStatus: collectionData.currentReadStatus, lastReadChanged: now }
      : {}),
    ...(collectionData.miscOwnedMap ? { miscItemStatus: collectionData.miscOwnedMap } : {}),
    ...(possessionChanged ? { lastCollectionChanged: now } : {}),
  }
}

export const useSyncingTool = (client: typeof apolloClient): CollectionSyncState => {
  const { authState } = storeToRefs(useAuthStore())
  const syncStatus = ref<SyncStatus | null>(null)

  const syncToServer = async <T extends DocumentNode>(
    mutation: T,
    variables: VariablesOf<T>,
  ): Promise<ResultOf<T>> => {
    const { errors, data } = await client.mutate({
      mutation,
      variables: variables as unknown as never,
    })
    if (errors) {
      syncStatus.value = {
        type: 'error',
        syncId: syncStatus.value!.syncId,
        error: { type: 'unknown', error: new Error(errors.join('\n')) },
      }
      return Promise.reject(errors)
    } else {
      client.cache.evict({ fieldName: 'userReleaseEntryStatuses' })
      client.cache.evict({ fieldName: 'filteredNovelties' })
      client.cache.evict({ fieldName: 'filteredUserReleaseEntryStatuses' })
      client.cache.gc()
      return data
    }
  }

  const startSync = async (action: UserCollectionActionSync, resolved: ResolvedServerIds) => {
    try {
      if (!authState.value.userAdmin?.user?.id) {
        syncStatus.value = {
          type: 'error',
          syncId: action.sync.id,
          error: { type: 'unknown', error: new Error('Will not sync, no user exists!') },
        }
        return
      }
      if (syncStatus.value?.type === 'inProgress' || action.sync.synced) {
        console.warn(
          `Will not sync ${action.sync.id}. Status is "${syncStatus.value?.type}" - "${action.sync.synced ? 'Already' : 'Not yet'} synced.".`,
        )
        return
      }
      syncStatus.value = { type: 'inProgress', syncId: action.sync.id }
      const now = new Date().toISOString()
      await match(action.type)
        .returnType<Promise<void>>()
        .with({ type: 'createCollectionEntry' }, async ({ data }) => {
          const result = await syncToServer(CREATE_USER_COLLECTION, {
            data: buildCreateUserCollectionInput(data, now),
          })
          if (
            result.createUserReleaseEntryStatus?.id &&
            result.createUserReleaseEntryStatus?.readStatus?.[0]?.id
          ) {
            syncStatus.value = {
              type: 'creationSuccess',
              syncId: action.sync.id,
              creationResult: {
                collectionId: result.createUserReleaseEntryStatus.id,
                latestReadId: result.createUserReleaseEntryStatus.readStatus[0].id,
              },
            }
          } else {
            syncStatus.value = {
              type: 'error',
              syncId: action.sync.id,
              error: { type: 'unknown', error: new Error('No collection or read id returned') },
            }
          }
        })
        .with({ type: 'removeCollectionEntry' }, async ({ data: { deletionDate } }) => {
          if (!resolved.collectionId) {
            throw new Error('collectionId must be resolved to remove a collection entry')
          }
          await syncToServer(REMOVE_USER_COLLECTION, {
            where: { id: resolved.collectionId },
            eventDate: deletionDate,
          })
          syncStatus.value = { type: 'success', syncId: action.sync.id }
        })
        .with({ type: 'updateCollectionData' }, async ({ data: { collectionData } }) => {
          if (!resolved.collectionId) {
            throw new Error('collectionId must be resolved to update collection data')
          }
          await syncToServer(UPDATE_USER_COLLECTION, {
            where: { id: resolved.collectionId },
            data: buildUpdateUserCollectionInput(collectionData, now),
          })
          syncStatus.value = { type: 'success', syncId: action.sync.id }
        })
        .with({ type: 'updateEntryReadMap' }, async ({ data }) => {
          if (!resolved.readId) {
            throw new Error('readId must be resolved to update read status')
          }
          await syncToServer(UPDATE_USER_READ_STATUS, {
            where: { id: resolved.readId },
            data: {
              readStatus: data.latestReadMap,
              lastReadChanged: now,
            },
          })
          syncStatus.value = { type: 'success', syncId: action.sync.id }
        })
        .exhaustive()
    } catch (e) {
      if (e instanceof Error) {
        syncStatus.value = {
          type: 'error',
          syncId: action.sync.id,
          error: { type: isServerDownError(e) ? 'serverDown' : 'unknown', error: e },
        }
      } else {
        throw e
      }
    }
  }

  return {
    syncStatus,
    startSync,
  }
}
